import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import axios from "axios";

type User = {
  username: string;
  password: string;
};

type NewUser = User & {
  name: string;
};

type UserBasicInfo = {
  id: string;
  name: string;
  username: string;
  gameStage: number;
};

type UserProfileData = {
  name: string;
  username: string;
};

type AuthApiState = {
  basicUserInfo?: UserBasicInfo | null;
  userProfileData?: UserProfileData | null;
  status: "idle" | "loading" | "failed";
  error: string | null;
};

const initialState: AuthApiState = {
  basicUserInfo: localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo") as string)
    : null,
  userProfileData: undefined,
  status: "idle",
  error: null,
};

export const login = createAsyncThunk("login", async (data: User) => {
  const response = await axiosInstance.post("/login", data);
  const resData = response.data;

  localStorage.setItem("userInfo", JSON.stringify(resData));

  return resData;
});

export const register = createAsyncThunk("register", async (data: NewUser) => {
  const response = await axiosInstance.post(
    "/register",
    data
  );
  const resData = response.data;

  localStorage.setItem("userInfo", JSON.stringify(resData));

  return resData;
});

export const logout = createAsyncThunk("logout", async () => {
  const response = await axiosInstance.post("/logout", {});
  const resData = response.data;

  localStorage.removeItem("userInfo");

  return resData;
});

export const getUser = createAsyncThunk(
  "users/profile",
  async (userId: string) => {
    const response = await axiosInstance.get(
      `/users/${userId}`
    );
    return response.data;
  }
);

export const updateGameStage = createAsyncThunk(
    "auth/updateGameStage",
    async (newStage: number, { rejectWithValue }) => {
        try {
            console.log("Sending PUT to /pinboard/me with:", newStage);
            const response = await axiosInstance.put(
                "/pinboard/me",
                { gameStage: newStage },
                { withCredentials: true }
            );
            console.log("Response from server:", response.data);
            return response.data.gameStage;
        } catch (err: any) {
            console.error("Update game stage failed:", err.response);
            return rejectWithValue(err.response?.data || "Update failed");
        }
    }
);

export const fetchGameStage = createAsyncThunk(
    "auth/fetchGameStage",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/pinboard/me", {
                withCredentials: true,
            });
            return response.data.gameStage;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || "Failed to fetch game stage");
        }
    }
);



const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<UserBasicInfo>) => {
          state.status = "idle";
          state.basicUserInfo = action.payload;
        }
      )
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Login failed";
      })

      .addCase(register.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        register.fulfilled,
        (state, action: PayloadAction<UserBasicInfo>) => {
          state.status = "idle";
          state.basicUserInfo = action.payload;
        }
      )
      .addCase(register.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Registration failed";
      })

      .addCase(logout.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.status = "idle";
        state.basicUserInfo = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Logout failed";
      })

      .addCase(getUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.userProfileData = action.payload;
      })
      .addCase(updateGameStage.pending, (state) => {
      state.status = "loading";
      state.error = null;
      })
      .addCase(updateGameStage.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload as string || "Failed to update game stage";
      })
      .addCase(getUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Get user profile data failed";
      })
      .addCase(fetchGameStage.fulfilled, (state, action) => {
        if (state.basicUserInfo) {
        state.basicUserInfo.gameStage = action.payload;
      }
      })
      .addCase(updateGameStage.fulfilled, (state, action) => {
                if (state.basicUserInfo) {
                    state.basicUserInfo.gameStage = action.payload;
                }
            });
      
  },
});

export default authSlice.reducer;